/**
 * SalesStatusContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import SalesStatusPresenter from './SalesStatusPresenter';
import { useGetOrders } from 'hooks/OrderHooks';
import { useGetMenus } from 'hooks/MenuHooks';
import { useGetMenuCategories } from 'hooks/ProductCategoryHooks';
import moment from 'moment';
import dayjs from 'dayjs';
import { useUserStack } from 'store';

const SalesStatusContainer = () => {
  /* ===== DATA ===== */

  // 메뉴 전체 조회
  const { data: Menus } = useGetMenus({});
  // 메뉴 카테고리 전체 조회
  const { data: Categories } = useGetMenuCategories();

  /* ===== Mutate ===== */
  const userInfo = useUserStack();
  // 오더 전체 조회
  /* ===== STATE ===== */

  // 초기에는 "이번 달 1일 ~ 이번 달 말일"로 설정
  const [startOfToday, setStartOfToday] = useState(
    dayjs().startOf('month').unix(),
  );
  const [endOfToday, setEndOfToday] = useState(dayjs().endOf('month').unix());

  // 해당 월의 데이터만 가져오도록 설정
  // start_date, end_date가 바뀔 때마다 자동으로 refetch
  const { data: Orders, refetch } = useGetOrders({
    start_date: startOfToday,
    end_date: endOfToday,
  });

  // const { data: Orders } = useGetOrders({});
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [categoryData, setCategoryData] = React.useState([]);
  // 메뉴 매출 데이터
  const [menuSales, setMenuSales] = useState([]);

  const todayString = dayjs().format('YYYY-MM-DD');
  const [selectday, setSelectday] = useState(todayString);
  // 일별 매출 상태
  const [dailySales, setDailySales] = useState({});
  const [dailyRefundSales, setDailyRefundSales] = useState({});
  const [dailyCouponSales, setDailyCouponSales] = useState({});
  // 모달
  const [isOpened, setIsOpened] = React.useState(false);
  const [modalType, setModalType] = React.useState('');

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  const createCategoryMap = (categories) => {
    return categories?.reduce((acc, category) => {
      acc[category?.product_category_id] = category?.product_category_name;
      return acc;
    }, {});
  };

  const getSortedMenuData = (orders) => {
    const newOrders = orders?.filter((item) => {
      // 기본 조건: payment_state가 'COMPLETED'인 경우
      const isPaymentCompleted = item?.payment_state === 'COMPLETED';

      // 선택된 날짜가 있을 경우 날짜 필터링 추가
      const isDateMatching = selectday
        ? moment.unix(item.created_at).format('YYYY-MM-DD') === selectday
        : true;

      return isPaymentCompleted && isDateMatching;
    });
    // 메뉴 이름별 판매 수량 합산 및 ID, 가격 유지
    const menuSales =
      newOrders &&
      newOrders.reduce((acc, order) => {
        order.product_information.forEach((menu) => {
          const { menu_id = '-', menu_name, num, price } = menu;

          if (acc[menu_name]) {
            acc[menu_name].num += num; // 수량 합산
          } else {
            acc[menu_name] = {
              menu_id,
              menu_name,
              num,
              price,
            }; // 처음 추가
          }
        });
        return acc;
      }, {});

    const categoryMap = createCategoryMap(Categories?.data);

    const mappedData = Menus?.data?.map((menu) => {
      return {
        menu_id: menu?.menu_id,
        menu_name: menu?.menu_name,
        price: menu?.price,
        product_category_id: menu?.product_category_id,
        product_category_name: categoryMap[menu?.product_category_id] || '-',
      };
    });

    const sortedMenuArray =
      menuSales &&
      Object.entries(menuSales)
        .map(([menu_name, { menu_id, num, price }]) => {
          const categoryData = mappedData.find(
            (menu) => menu.menu_id === menu_id,
          );
          return {
            menu_name,
            menu_id,
            num,
            price,
            product_category_id: categoryData?.product_category_id || '-',
            product_category_name: categoryData?.product_category_name || '-',
          };
        })
        .sort((a, b) => b.num - a.num); // 수량을 기준으로 내림차순 정렬

    return sortedMenuArray;
  };

  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '') => {
    setIsOpened(!open);
    setModalType(type);
  };
  /* ===== HOOKS ===== */

  /**
   * 카테고리 관련 데이터 정제
   * --
   */
  useEffect(() => {
    // 상위 카테고리 및 하위 카테고리 그룹화
    const categoryMap = Categories?.data?.reduce((acc, category) => {
      if (!category.parents_id) {
        acc[category.product_category_id] = {
          ...category,
          key: category?.product_category_id, // key 추가
          title: category?.product_category_name, // title 추가
          children: [],
          menus: [], // 상위 카테고리의 메뉴 배열 초기화
        };
      } else {
        const parentCategory = acc[category.parents_id];
        if (parentCategory) {
          parentCategory.children.push({
            ...category,
            key: category?.product_category_id, // key 추가
            title: category?.product_category_name, // title 추가
            menus: [], // 하위 카테고리의 메뉴 배열 초기화
          });
        }
      }
      return acc;
    }, {});
    // 카테고리 데이터를 배열로 변환하여 업데이트
    const newData = Object.values(categoryMap || [])?.sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );

    setCategoryData(newData);
  }, []);

  /**
   * 오더 데이터 정제
   * --
   */
  useEffect(() => {
    const orders = Orders?.data?.filter(
      (item) => item?.shop_information_id === userInfo?.shop_information_id,
    );
    const menuData = getSortedMenuData(orders);

    if (orders) {
      const { salesData, refundedData, couponDiscountData } = orders?.reduce(
        (acc, order) => {
          const date = moment.unix(order.created_at).format('YYYY-MM-DD');

          if (order?.payment_state === 'COMPLETED') {
            acc.salesData[date] =
              (acc.salesData[date] || 0) + order.final_price;
          }

          if (order?.payment_state === 'REFUNDED') {
            acc.refundedData[date] =
              (acc.refundedData[date] || 0) + order.final_price;
          }

          if (order?.payment_state === 'COMPLETED') {
            if (order?.coupon_info && Array.isArray(order.coupon_info)) {
              const totalDiscount = order.coupon_info.reduce(
                (sum, coupon) => sum + (coupon.discount_price || 0),
                0,
              );
              acc.couponDiscountData[date] =
                (acc.couponDiscountData[date] || 0) + totalDiscount;
            }
          }

          return acc;
        },
        { salesData: {}, refundedData: {}, couponDiscountData: {} }, // 초기값 설정
      );
      setDailySales(salesData);
      setDailyRefundSales(refundedData);
      setDailyCouponSales(couponDiscountData);
    }

    setMenuSales(menuData);
  }, [Orders, selectday]);

  /* ===== RENDER ===== */
  return (
    <>
      <SalesStatusPresenter
        menuSales={menuSales}
        categoryData={categoryData}
        setSelectday={setSelectday}
        isOpened={isOpened}
        onOpen={handleModal}
        dailySales={dailySales}
        dailyRefundSales={dailyRefundSales}
        dailyCouponSales={dailyCouponSales}
        modalType={modalType}
        setStartOfToday={setStartOfToday}
        setEndOfToday={setEndOfToday}
      />
    </>
  );
};

export default SalesStatusContainer;
